import type { ReactNode } from 'react'
import type { TradingSignal } from '../../types'
import CoinIcon from '../CoinIcon'
import { formatPrice } from '../../utils/marketPrice'
import { formatPnlPct } from '../../utils/pnl'
import { signalExpiry } from '../../utils/signalExpiry'
import { exitReason, signalPnlPct } from '../../utils/signalPnl'
import CallLink from './CallLink'
import { DirectionChip, ExcludedChip, ExitReasonChip, isOpenSignal, pnlText } from './signalChips'
import { displaySymbol } from '../../utils/displaySymbol'

const Cell = ({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) => (
  <span className={`flex min-w-[4.5rem] flex-col ${className}`}>
    <span className="text-[10px] uppercase tracking-wider text-gray-500">{label}</span>
    <span className="font-mono text-sm tabular-nums text-gray-100">{children}</span>
  </span>
)

interface SignalRowProps {
  signal: TradingSignal
  /** Latest price for an open signal (the exit price is used for a finished one). */
  price: number | null
  selected: boolean
  now: number
  onSelect: () => void
}

/**
 * One signal as a compact row. Open: live P&L and time left. Finished: exit
 * reason and final P&L. Clicking loads it into the expanded card above; the
 * permalink beside it opens the call's public page.
 */
const SignalRow = ({ signal, price, selected, now, onSelect }: SignalRowProps) => {
  const open = isOpenSignal(signal)
  const reason = open ? null : exitReason(signal)
  // Open: marked to the live price. Closed: the P&L the backend recorded at the
  // exit (already from the position's side), expiries included. Cancelled: no result.
  const pnl = open ? signalPnlPct(signal, price) : signal.status === 'closed' ? signal.profit_loss_percent ?? null : null
  const expiry = open ? signalExpiry(signal, now) : null

  return (
    <li className="flex items-stretch gap-2">
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={`flex min-w-0 flex-1 flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border px-3 py-2.5 text-left transition-colors ${
          selected
            ? 'border-accent/70 bg-accent/10'
            : 'border-terminal-border bg-terminal-surface hover:border-gray-500/60 hover:bg-white/5'
        } ${signal.status === 'cancelled' ? 'opacity-70' : ''}`}
        data-testid="signal-row"
        data-status={signal.status}
      >
        <span className="flex min-w-[11rem] flex-1 items-center gap-2 sm:flex-none">
          <CoinIcon symbol={signal.symbol} size={22} />
          <span className="font-semibold text-gray-50">{displaySymbol(signal.symbol)}</span>
          <DirectionChip direction={signal.signal_type} />
          <span className="text-xs text-gray-400">{signal.timeframe}</span>
        </span>

        <Cell label="Entry">${formatPrice(signal.entry_price)}</Cell>
        <Cell label={open ? 'P&L' : 'Final P&L'}>
          <span className={`font-semibold ${pnlText(pnl)}`} data-testid="row-pnl">
            {pnl == null ? '—' : formatPnlPct(pnl)}
          </span>
        </Cell>
        <Cell label="Target">${formatPrice(signal.take_profit)}</Cell>
        <Cell label="Stop">${formatPrice(signal.stop_loss)}</Cell>
        {open ? (
          <Cell label="Time left">
            <span className={expiry?.state === 'expired' ? 'text-red-300' : expiry?.state === 'soon' ? 'text-amber-300' : ''} data-testid="row-expiry">
              {expiry ? expiry.label.replace(/^expires in /, '') : '—'}
            </span>
          </Cell>
        ) : (
          <Cell label="Exit">{reason ? <ExitReasonChip reason={reason} /> : '—'}</Cell>
        )}

        <span className="ml-auto flex items-center gap-1.5">
          {signal.excluded_from_record && <ExcludedChip note={signal.exclusion_note} />}
          {selected && (
            <span className="rounded bg-accent/25 px-1.5 py-0.5 text-[11px] font-semibold text-accent" data-testid="viewing-chip">
              Viewing
            </span>
          )}
        </span>
      </button>
      <span className="flex items-center">
        <CallLink id={signal.id} className="text-xs" />
      </span>
    </li>
  )
}

export default SignalRow
